import { useEffect, useRef, useState } from 'react';
import { Container, Button, Card, Spinner, Alert } from 'react-bootstrap';
import { useOktaAuth } from '@okta/okta-react';

/**
 * Auto-initiates the OKTA redirect once the SDK has settled.
 *
 * Drives directly off okta-react's `authState` (not an abstracted loading flag) so the
 * state machine is explicit:
 *   - authState === null       -> SDK still initializing; wait (show spinner).
 *   - authState.isAuthenticated -> already signed in; nothing to do here (Gated routes on).
 *   - otherwise                -> no session; start the Authorization Code + PKCE redirect.
 *
 * A manual button + any error are shown so a failed/early redirect never leaves a blank wait.
 */
export default function Login() {
  const { oktaAuth, authState } = useOktaAuth();
  const started = useRef(false);
  const [err, setErr] = useState<string | null>(null);

  async function signIn() {
    try {
      setErr(null);
      await oktaAuth.signInWithRedirect();
    } catch (e) {
      started.current = false; // re-arm so the button / next render can retry
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    if (started.current) return;
    if (!authState) return; // SDK not settled yet
    if (authState.isAuthenticated) return; // already in; Gated will route to the app
    started.current = true;
    void signIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  return (
    <Container className="py-5" style={{ maxWidth: 440 }}>
      <Card>
        <Card.Body className="text-center">
          <Card.Title>vuln-sentinel</Card.Title>
          {err ? (
            <Alert variant="danger" className="text-start">
              Sign-in couldn't start automatically: {err}
            </Alert>
          ) : (
            <>
              <p className="text-muted">
                {authState ? 'Redirecting to Genesys OKTA sign-in…' : 'Starting up…'}
              </p>
              <Spinner animation="border" role="status" className="mb-3" />
            </>
          )}
          <div>
            <Button variant="outline-primary" size="sm" onClick={signIn}>
              Sign in with OKTA
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}
